import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, UserRole } from '../../types';
import { useAuth } from './AuthContext';
import { dataService } from '../../services/dataService';
import { OPERATIONS_MODULE_ENABLED } from '../lib/featureFlags';
import { ROUTES, PORTAL_ROUTES } from '../routes';

/**
 * Interactive tour engine: replaces the old OnboardingTour.tsx (text modal disconnected
 * from the UI) with a real spotlight anchored to existing app elements, navigating
 * page by page along each role's full workflow. See components/TourSpotlight.tsx for the
 * visual overlay -- this file only resolves STATE (which step, which element it points
 * at, when that element already exists in the DOM).
 */

export type TourRoleKey = 'client' | 'sales_rep' | 'operations' | 'accounting' | 'supervision';

export interface TourStep {
  id: string;
  route?: string;
  targetSelector: string;
  fallbackSelector?: string;
  titleKey: string;
  descriptionKey: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  action?: 'navigate' | 'observe';
  visibleIf?: (user: User) => boolean;
  // String name of a lucide-react icon (see src/lib/tourIcons.tsx), optional -- a step
  // without iconName falls back to the generic icon; nothing breaks.
  iconName?: string;
}

const ROLE_TO_TOUR: Record<UserRole, TourRoleKey> = {
  client: 'client',
  sales_rep: 'sales_rep',
  operations: 'operations',
  accounting: 'accounting',
  ceo: 'supervision',
  admin: 'supervision',
  adminapp: 'supervision',
 // Agency network (design spec §3): a reduced-interface role, closest in shape to operations
 // (creates guides/pickups) -- no dedicated tour content yet, reuses that workflow's tour.
  agencia_operador: 'operations',
};

/** The user can have several roles -- the most specific workflow one is picked (not adminapp by
 * default) so the tour talks about what that user actually does day to day. */
export const resolveTourRole = (roles: UserRole[]): TourRoleKey => {
  const priority: UserRole[] = ['client', 'sales_rep', 'operations', 'accounting', 'ceo', 'admin', 'adminapp'];
  const match = priority.find(r => roles.includes(r));
  return ROLE_TO_TOUR[match || 'adminapp'];
};

const PAGE_ROOT = '[data-tour-id="page-root"]';

export const TOUR_FLOWS: Record<TourRoleKey, TourStep[]> = {
  client: [
    { id: 'cliente-home', targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.client.steps.0.title', descriptionKey: 'home.onboarding.client.steps.0.description', iconName: 'Home' },
    { id: 'cliente-portal', targetSelector: `[data-tour-id="${ROUTES.clientPortal}"]`, titleKey: 'home.onboarding.client.steps.1.title', descriptionKey: 'home.onboarding.client.steps.1.description', iconName: 'Users' },
    { id: 'cliente-nueva-cotizacion', route: `${ROUTES.clientPortal}/${PORTAL_ROUTES.newQuote}`, targetSelector: '[data-tour-id="portal-nueva-cotizacion-submit"]', fallbackSelector: PAGE_ROOT, titleKey: 'home.onboarding.client.steps.2.title', descriptionKey: 'home.onboarding.client.steps.2.description', action: 'observe', iconName: 'FileEdit' },
    { id: 'cliente-aprobacion', route: `${ROUTES.clientPortal}/${PORTAL_ROUTES.quoteHistory}`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.client.steps.3.title', descriptionKey: 'home.onboarding.client.steps.3.description', iconName: 'History' },
    { id: 'cliente-seguimiento', route: `${ROUTES.clientPortal}/${PORTAL_ROUTES.tracking}`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.client.steps.4.title', descriptionKey: 'home.onboarding.client.steps.4.description', iconName: 'Truck' },
    { id: 'cliente-saldo', route: `${ROUTES.clientPortal}/${PORTAL_ROUTES.pendingBalance}`, targetSelector: '[data-tour-id="saldo-pendiente-detalle"]', fallbackSelector: PAGE_ROOT, titleKey: 'home.onboarding.client.steps.5.title', descriptionKey: 'home.onboarding.client.steps.5.description', iconName: 'Wallet' },
    { id: 'cliente-historial-operaciones', route: `${ROUTES.clientPortal}/${PORTAL_ROUTES.operationHistory}`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.client.steps.6.title', descriptionKey: 'home.onboarding.client.steps.6.description', iconName: 'History' },
    // 16/08: the fiscal documents section has had its own menu entry since 11/08,
    // but the tour never mentioned it -- the client did not know where to download invoices.
    { id: 'cliente-documentos-fiscales', route: `${ROUTES.clientPortal}/${PORTAL_ROUTES.myDocuments}`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.client.steps.7.title', descriptionKey: 'home.onboarding.client.steps.7.description', iconName: 'FileCheck2' },
  ],
  sales_rep: [
    { id: 'comercial-home', targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.salesRep.steps.0.title', descriptionKey: 'home.onboarding.salesRep.steps.0.description', iconName: 'Home' },
    { id: 'comercial-crear-cotizacion', route: `${ROUTES.quote}?view=form`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.salesRep.steps.1.title', descriptionKey: 'home.onboarding.salesRep.steps.1.description', iconName: 'FileEdit' },
    { id: 'comercial-esperar-aprobacion', route: `${ROUTES.quote}?view=history`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.salesRep.steps.2.title', descriptionKey: 'home.onboarding.salesRep.steps.2.description', iconName: 'History' },
    { id: 'comercial-registrar-operacion', route: `${ROUTES.quote}?view=history`, targetSelector: '[data-tour-id="quote-register-operation"]', fallbackSelector: PAGE_ROOT, titleKey: 'home.onboarding.salesRep.steps.3.title', descriptionKey: 'home.onboarding.salesRep.steps.3.description', visibleIf: () => OPERATIONS_MODULE_ENABLED, iconName: 'PackagePlus' },
    { id: 'comercial-ver-guia', route: `${ROUTES.operations}?view=history`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.salesRep.steps.4.title', descriptionKey: 'home.onboarding.salesRep.steps.4.description', visibleIf: () => OPERATIONS_MODULE_ENABLED, iconName: 'Truck' },
    { id: 'comercial-comisiones-pendientes', route: `${ROUTES.commissions}?status=pending_payment`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.salesRep.steps.5.title', descriptionKey: 'home.onboarding.salesRep.steps.5.description', iconName: 'DollarSign' },
    { id: 'comercial-comisiones-liquidadas', route: `${ROUTES.commissions}?status=liquidated`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.salesRep.steps.6.title', descriptionKey: 'home.onboarding.salesRep.steps.6.description', iconName: 'DollarSign' },
    { id: 'comercial-clientes', route: ROUTES.clients, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.salesRep.steps.7.title', descriptionKey: 'home.onboarding.salesRep.steps.7.description', iconName: 'Users' },
  ],
  // Reordered and expanded (16/08) to follow the same order as the side menu, which in turn
  // follows a real operations day: first incoming email, then pickup coordination, and only
  // then quoting / generating shipments. Previously the tour started at the Quoter and never
  // mentioned Mailbox, Pickups, Cases, or Air Freight -- four full modules the user had to
  // discover alone.
  operations: [
    { id: 'operaciones-home', targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.operations.steps.0.title', descriptionKey: 'home.onboarding.operations.steps.0.description', iconName: 'Home' },
    { id: 'operaciones-buzon', route: ROUTES.mailbox, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.operations.steps.1.title', descriptionKey: 'home.onboarding.operations.steps.1.description', iconName: 'Mail' },
    // Points at the full menu item, not the badge: the badge only renders when there are
    // unread emails, and a selector that sometimes does not exist would leave the step
    // always on fallback.
    { id: 'operaciones-buzon-badge', targetSelector: `[data-tour-id="${ROUTES.mailbox}"]`, fallbackSelector: PAGE_ROOT, titleKey: 'home.onboarding.operations.steps.2.title', descriptionKey: 'home.onboarding.operations.steps.2.description', iconName: 'Mail' },
    { id: 'operaciones-recolecciones', route: ROUTES.pickups, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.operations.steps.3.title', descriptionKey: 'home.onboarding.operations.steps.3.description', iconName: 'CalendarClock' },
    { id: 'operaciones-cotizador', route: `${ROUTES.quote}?view=form`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.operations.steps.4.title', descriptionKey: 'home.onboarding.operations.steps.4.description', iconName: 'FileEdit' },
    { id: 'operaciones-descuento', route: `${ROUTES.quote}?view=form`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.operations.steps.5.title', descriptionKey: 'home.onboarding.operations.steps.5.description', iconName: 'PercentCircle' },
    { id: 'operaciones-generar-guia', route: `${ROUTES.operations}?view=form`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.operations.steps.6.title', descriptionKey: 'home.onboarding.operations.steps.6.description', visibleIf: () => OPERATIONS_MODULE_ENABLED, iconName: 'PackagePlus' },
    { id: 'operaciones-historial', route: `${ROUTES.operations}?view=history`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.operations.steps.7.title', descriptionKey: 'home.onboarding.operations.steps.7.description', visibleIf: () => OPERATIONS_MODULE_ENABLED, iconName: 'History' },
    { id: 'operaciones-facturas-pendientes', route: `${ROUTES.operations}?view=history`, targetSelector: '[data-tour-id="facturas-pendientes"]', fallbackSelector: PAGE_ROOT, titleKey: 'home.onboarding.operations.steps.8.title', descriptionKey: 'home.onboarding.operations.steps.8.description', visibleIf: () => OPERATIONS_MODULE_ENABLED, iconName: 'FileWarning' },
    { id: 'operaciones-casos', route: ROUTES.cases, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.operations.steps.9.title', descriptionKey: 'home.onboarding.operations.steps.9.description', iconName: 'LifeBuoy' },
    { id: 'operaciones-reversibilidad', route: `${ROUTES.operations}?view=history`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.operations.steps.11.title', descriptionKey: 'home.onboarding.operations.steps.11.description', visibleIf: () => OPERATIONS_MODULE_ENABLED, iconName: 'RotateCcw' },
  ],
  // Expanded (16/08) to cover the full accounting cycle as worked today: the accounting
  // mailbox where invoices and documents come from, the 4 work queues inside Accounting,
  // real issue/void in Siigo, and an explicit close on what can be undone. `accounting` and
  // `billing` are the same role in the system, so this flow is the only one both people see.
  accounting: [
    { id: 'contabilidad-home', targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.0.title', descriptionKey: 'home.onboarding.accounting.steps.0.description', iconName: 'Home' },
    // New step (17/08): the module went from a flat list of 10 views to grouped submodules,
    // and without explaining it the user does not know the top bar is what drives navigation.
    { id: 'contabilidad-organizacion', route: `${ROUTES.accounting}?view=logistrade`, targetSelector: '[data-tour-id="accounting-submodule-nav"]', fallbackSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.1.title', descriptionKey: 'home.onboarding.accounting.steps.1.description', iconName: 'Compass' },
    { id: 'contabilidad-buzon', route: ROUTES.mailbox, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.2.title', descriptionKey: 'home.onboarding.accounting.steps.2.description', iconName: 'Mail' },
    { id: 'contabilidad-registrar-pago', route: `${ROUTES.accounting}?view=pagos`, targetSelector: '[data-tour-id="payments-new"]', fallbackSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.3.title', descriptionKey: 'home.onboarding.accounting.steps.3.description', iconName: 'Wallet' },
    { id: 'contabilidad-asignar-pago', route: `${ROUTES.accounting}?view=pagos`, targetSelector: '[data-tour-id="payments-assign"]', fallbackSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.4.title', descriptionKey: 'home.onboarding.accounting.steps.4.description', iconName: 'Wallet' },
    { id: 'contabilidad-recaudo', route: `${ROUTES.accounting}?view=recaudo`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.5.title', descriptionKey: 'home.onboarding.accounting.steps.5.description', iconName: 'Landmark' },
    { id: 'contabilidad-saldos-favor', route: `${ROUTES.accounting}?view=saldos`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.6.title', descriptionKey: 'home.onboarding.accounting.steps.6.description', iconName: 'PiggyBank' },
    { id: 'contabilidad-por-facturar', route: `${ROUTES.accounting}?view=porFacturar`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.7.title', descriptionKey: 'home.onboarding.accounting.steps.7.description', iconName: 'Receipt' },
    { id: 'contabilidad-facturas-operador', route: `${ROUTES.accounting}?view=facturasOperador`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.8.title', descriptionKey: 'home.onboarding.accounting.steps.8.description', iconName: 'FileWarning' },
    { id: 'contabilidad-correo-facturas', route: `${ROUTES.accounting}?view=correoFacturas`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.9.title', descriptionKey: 'home.onboarding.accounting.steps.9.description', iconName: 'Scale' },
    { id: 'contabilidad-siigo', route: `${ROUTES.accounting}?view=siigo`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.10.title', descriptionKey: 'home.onboarding.accounting.steps.10.description', iconName: 'RefreshCw' },
    { id: 'contabilidad-conciliacion-siigo', route: `${ROUTES.accounting}?view=conciliacionSiigo`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.11.title', descriptionKey: 'home.onboarding.accounting.steps.11.description', iconName: 'Scale' },
    { id: 'contabilidad-facturar-anular', route: `${ROUTES.accounting}?view=logistrade`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.12.title', descriptionKey: 'home.onboarding.accounting.steps.12.description', iconName: 'Receipt' },
    { id: 'contabilidad-liquidar-comisiones', route: `${ROUTES.commissions}?status=pending_commission`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.13.title', descriptionKey: 'home.onboarding.accounting.steps.13.description', iconName: 'DollarSign' },
    { id: 'contabilidad-reversibilidad', route: `${ROUTES.accounting}?view=logistrade`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.accounting.steps.14.title', descriptionKey: 'home.onboarding.accounting.steps.14.description', iconName: 'RotateCcw' },
  ],
  supervision: [
    { id: 'supervision-home', targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.supervision.steps.0.title', descriptionKey: 'home.onboarding.supervision.steps.0.description', iconName: 'Home' },
    { id: 'supervision-dashboard', route: ROUTES.dashboard, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.supervision.steps.1.title', descriptionKey: 'home.onboarding.supervision.steps.1.description', iconName: 'LayoutDashboard' },
    { id: 'supervision-billing-report', route: `${ROUTES.billingReport}/dashboard`, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.supervision.steps.2.title', descriptionKey: 'home.onboarding.supervision.steps.2.description', iconName: 'BarChart3' },
    { id: 'supervision-auditoria', route: ROUTES.audit, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.supervision.steps.3.title', descriptionKey: 'home.onboarding.supervision.steps.3.description', iconName: 'ShieldCheck' },
    { id: 'supervision-administracion', route: ROUTES.salesReps, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.supervision.steps.4.title', descriptionKey: 'home.onboarding.supervision.steps.4.description', iconName: 'Briefcase' },
    { id: 'supervision-reversibilidad', route: ROUTES.audit, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.supervision.steps.5.title', descriptionKey: 'home.onboarding.supervision.steps.5.description', iconName: 'RotateCcw' },
    { id: 'supervision-adminapp', route: ROUTES.settings, targetSelector: PAGE_ROOT, titleKey: 'home.onboarding.supervision.steps.6.title', descriptionKey: 'home.onboarding.supervision.steps.6.description', visibleIf: (user) => user.roles.includes('adminapp'), iconName: 'Settings' },
  ],
};

const SEEN_KEY_PREFIX = 'logistrade_onboarding_seen_';
const PROGRESS_KEY_PREFIX = 'logistrade_onboarding_progress_';

interface TourProgress {
  role: TourRoleKey;
  stepIndex: number;
}

interface ResolvedStep extends TourStep {
  usingFallback: boolean;
}

interface TourContextType {
  isActive: boolean;
  currentFlow: TourRoleKey | null;
  currentStepIndex: number;
  totalSteps: number;
  currentStep: ResolvedStep | null;
  waitingForTarget: boolean;
  hasSeenTour: boolean;
  savedProgress: TourProgress | null;
  // Highest index reached in the active flow (persisted in DB) -- backs the clickable
  // progress bar: you can never jump to a step ahead of this value.
  maxReachedIndex: number;
  // true while the exit confirmation modal is shown (manual navigation out of the tour)
  // instead of the previous silent close.
  exitPromptPending: boolean;
  startTour: (role: TourRoleKey, fromStep?: number) => void;
  stopTour: (opts?: { keepProgress?: boolean; dismissed?: boolean }) => void;
  nextStep: () => void;
  prevStep: () => void;
  jumpToStep: (index: number) => void;
  requestExitPrompt: () => void;
  resumeAfterExitPrompt: () => void;
  confirmPauseFromExitPrompt: () => void;
  confirmDismissFromExitPrompt: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

const TARGET_POLL_INTERVAL_MS = 100;
const TARGET_POLL_TIMEOUT_MS = 4000;

const readProgress = (userId: string): TourProgress | null => {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY_PREFIX + userId);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const writeProgress = (userId: string, progress: TourProgress | null) => {
  try {
    if (progress) localStorage.setItem(PROGRESS_KEY_PREFIX + userId, JSON.stringify(progress));
    else localStorage.removeItem(PROGRESS_KEY_PREFIX + userId);
  } catch { /* localStorage may fail in private mode -- the tour still works in memory */ }
};

// Best-effort by design: a network failure saving tour progress/analytics must never
// block or revert in-memory progress -- same resilience criteria as notifyUsers() in
// services/notifications.ts.
const persistProgressToServer = (roleKey: TourRoleKey, stepIndex: number, status: 'in_progress' | 'completed' | 'dismissed') => {
  dataService.saveTourProgress({ roleKey, stepIndex, status }).catch(() => {});
};

const logTourEvent = (roleKey: TourRoleKey, evento: 'started' | 'step_viewed' | 'completed' | 'abandoned' | 'dismissed', stepId?: string) => {
  dataService.logTourEvent({ roleKey, stepId, evento }).catch(() => {});
};

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isActive, setIsActive] = useState(false);
  const [currentFlow, setCurrentFlow] = useState<TourRoleKey | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [waitingForTarget, setWaitingForTarget] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const [savedProgress, setSavedProgress] = useState<TourProgress | null>(null);
  const [maxReachedIndex, setMaxReachedIndex] = useState(0);
  const [exitPromptPending, setExitPromptPending] = useState(false);
  // 'completed' persisted in DB -- separate from savedProgress (which only describes IN
  // PROGRESS progress) because hasSeenTour must stay true even when savedProgress is null
  // (tour already finished, no progress left to resume).
  const [hasSeenTourServer, setHasSeenTourServer] = useState<boolean | null>(null);

  const pollRef = useRef<{ interval: ReturnType<typeof setInterval> | null; timeout: ReturnType<typeof setTimeout> | null }>({ interval: null, timeout: null });
  // Expected route of the active step, used to detect manual navigation out of the flow
  // without relying on stale closures inside the location listener.
  const expectedRouteRef = useRef<string | undefined>(undefined);
  const activeRef = useRef(false);
  activeRef.current = isActive;
  // true while the tour ITSELF is navigating (goToStepIndex/jumpToStep/resumeAfterExitPrompt)
  // -- the guard below must ignore the resulting location.pathname change, or every normal
  // step advance with `route` would fire the "manual exit" modal on itself.
  const selfNavigatingRef = useRef(false);

  // DB<->localStorage migration (effectively once per user): if a DB row already exists for
  // the resolved role, that is the source of truth. If there is no DB row but there is
  // localStorage (user from before this migration), sync once to DB and only then clear
  // localStorage -- if the PUT fails, retry on the next mount because the "no DB row"
  // condition still holds.
  useEffect(() => {
    if (!user?.id) { setSavedProgress(null); setHasSeenTourServer(null); setMaxReachedIndex(0); return; }
    const userId = user.id;
    const roleKey = resolveTourRole(user.roles);

    let cancelled = false;
    dataService.getTourProgress().then(({ items }) => {
      if (cancelled) return;
      const serverRow = items.find(i => i.roleKey === roleKey);
      if (serverRow) {
        setHasSeenTourServer(serverRow.status === 'completed' || serverRow.status === 'dismissed');
        setMaxReachedIndex(serverRow.stepIndex);
        setSavedProgress(serverRow.status === 'in_progress' ? { role: roleKey, stepIndex: serverRow.stepIndex } : null);
        return;
      }

      // No DB row -- fall back to localStorage (compat with users from before this
      // migration) while syncing toward DB.
      const localSeen = localStorage.getItem(SEEN_KEY_PREFIX + userId) === '1';
      const localProgress = readProgress(userId);
      setHasSeenTourServer(localSeen);
      setSavedProgress(localProgress);
      setMaxReachedIndex(localProgress?.stepIndex || 0);

      if (localSeen) {
        dataService.saveTourProgress({ roleKey, stepIndex: 0, status: 'completed' })
          .then(() => { localStorage.removeItem(SEEN_KEY_PREFIX + userId); localStorage.removeItem(PROGRESS_KEY_PREFIX + userId); })
          .catch(() => {});
      } else if (localProgress) {
        dataService.saveTourProgress({ roleKey: localProgress.role, stepIndex: localProgress.stepIndex, status: 'in_progress' })
          .then(() => localStorage.removeItem(PROGRESS_KEY_PREFIX + userId))
          .catch(() => {});
      }
    }).catch(() => {
      // DB unavailable -- the tour keeps working with localStorage only, without
      // cross-device persistence until the endpoint responds again.
      if (cancelled) return;
      setHasSeenTourServer(localStorage.getItem(SEEN_KEY_PREFIX + userId) === '1');
      const localProgress = readProgress(userId);
      setSavedProgress(localProgress);
      setMaxReachedIndex(localProgress?.stepIndex || 0);
    });
    return () => { cancelled = true; };
  }, [user?.id]);

  const clearPoll = useCallback(() => {
    if (pollRef.current.interval) clearInterval(pollRef.current.interval);
    if (pollRef.current.timeout) clearTimeout(pollRef.current.timeout);
    pollRef.current.interval = null;
    pollRef.current.timeout = null;
  }, []);

  const visibleSteps = useCallback((flow: TourRoleKey): TourStep[] => {
    const steps = TOUR_FLOWS[flow];
    if (!user) return steps;
    return steps.filter(s => !s.visibleIf || s.visibleIf(user));
  }, [user]);

  const resolveTarget = useCallback((step: TourStep, onResolved: (usingFallback: boolean) => void, opts?: { justNavigated?: boolean }) => {
    clearPoll();
    setWaitingForTarget(true);
    setUsingFallback(false);

    const check = () => document.querySelector(step.targetSelector);
    // If navigate() was just called, the real DOM (including a page with a generic
    // targetSelector like page-root) may still be the PREVIOUS route -- react-router does
    // not unmount/remount synchronously with navigate(). Resolving immediately here would
    // find the old node and jump straight to "ready", which also disabled TourSpotlight's
    // click-suppression guard (only active while waitingForTarget is true) exactly when
    // it is most needed: during the real remount.
    const existing = opts?.justNavigated ? null : check();
    if (existing) {
      setWaitingForTarget(false);
      onResolved(false);
      return;
    }

    pollRef.current.interval = setInterval(() => {
      if (check()) {
        clearPoll();
        setWaitingForTarget(false);
        onResolved(false);
      }
    }, TARGET_POLL_INTERVAL_MS);

    pollRef.current.timeout = setTimeout(() => {
      clearPoll();
      setWaitingForTarget(false);
      // The specific target did not appear (e.g. "Register Operation" with no approved
      // quotes) -- anchor to the fallback (always present on every tour page) with an
      // explanatory message instead of hanging the tour or failing silently.
      if (step.fallbackSelector && document.querySelector(step.fallbackSelector)) {
        setUsingFallback(true);
        onResolved(true);
      } else {
        onResolved(true);
      }
    }, TARGET_POLL_TIMEOUT_MS);
  }, [clearPoll]);

  const goToStepIndex = useCallback((flow: TourRoleKey, index: number) => {
    const steps = visibleSteps(flow);
    const step = steps[index];
    if (!step) return;

    expectedRouteRef.current = step.route;
    setCurrentStepIndex(index);
    if (user?.id) writeProgress(user.id, { role: flow, stepIndex: index });
    setMaxReachedIndex(prev => Math.max(prev, index));
    persistProgressToServer(flow, index, 'in_progress');
    logTourEvent(flow, 'step_viewed', step.id);

    // The real "view" of a page (e.g. Quote.tsx history vs form) lives in the query
    // string (?view=history vs ?view=form) -- comparing pathname alone left the user on
    // the legacy view while the tour already thought it was on the next step. But comparing
    // the FULL search also fails: pages like Quote.tsx add their own params after mount
    // (e.g. &step=2 from their internal wizard) that the tour neither controls nor should
    // treat as "already on the right route, don't navigate" or as "manual exit".
    // Correct criteria: only require that each param THE STEP explicitly declares is
    // already present with the same value -- extra params the page adds on its own are
    // ignored.
    let needsNav = false;
    if (step.route) {
      const [routePath, routeQuery] = step.route.split('?');
      if (location.pathname !== routePath) {
        needsNav = true;
      } else if (routeQuery) {
        const requiredParams = new URLSearchParams(routeQuery);
        const currentParams = new URLSearchParams(location.search);
        needsNav = [...requiredParams.entries()].some(([k, v]) => currentParams.get(k) !== v);
      }
    }
    if (needsNav) {
      selfNavigatingRef.current = true;
      navigate(step.route!);
    }

    resolveTarget(step, () => {}, { justNavigated: needsNav });
  }, [visibleSteps, navigate, location.pathname, location.search, resolveTarget, user?.id]);

  const startTour = useCallback((role: TourRoleKey, fromStep = 0) => {
    clearPoll();
    setCurrentFlow(role);
    setIsActive(true);
    setMaxReachedIndex(prev => Math.max(prev, fromStep));
    logTourEvent(role, 'started');
    goToStepIndex(role, fromStep);
  }, [clearPoll, goToStepIndex]);

  const stopTour = useCallback((opts?: { keepProgress?: boolean; dismissed?: boolean }) => {
    clearPoll();
    setIsActive(false);
    setWaitingForTarget(false);
    setUsingFallback(false);
    setExitPromptPending(false);
    expectedRouteRef.current = undefined;
    if (opts?.dismissed && currentFlow) {
      persistProgressToServer(currentFlow, currentStepIndex, 'dismissed');
      logTourEvent(currentFlow, 'dismissed');
      setHasSeenTourServer(true);
    } else if (!opts?.keepProgress && currentFlow) {
      logTourEvent(currentFlow, 'abandoned');
    }
    if (!opts?.keepProgress && user?.id) {
      writeProgress(user.id, null);
      setSavedProgress(null);
    } else if (user?.id) {
      setSavedProgress(readProgress(user.id));
    }
  }, [clearPoll, user?.id, currentFlow, currentStepIndex]);

  const nextStep = useCallback(() => {
    if (!currentFlow) return;
    const steps = visibleSteps(currentFlow);
    const isLast = currentStepIndex >= steps.length - 1;
    if (isLast) {
      if (user?.id) {
        localStorage.setItem(SEEN_KEY_PREFIX + user.id, '1');
        writeProgress(user.id, null);
        setSavedProgress(null);
      }
      persistProgressToServer(currentFlow, currentStepIndex, 'completed');
      logTourEvent(currentFlow, 'completed');
      setHasSeenTourServer(true);
      clearPoll();
      setIsActive(false);
      setWaitingForTarget(false);
      setUsingFallback(false);
      setExitPromptPending(false);
      expectedRouteRef.current = undefined;
      return;
    }
    goToStepIndex(currentFlow, currentStepIndex + 1);
  }, [currentFlow, currentStepIndex, visibleSteps, goToStepIndex, clearPoll, user?.id]);

  const prevStep = useCallback(() => {
    if (!currentFlow || currentStepIndex === 0) return;
    goToStepIndex(currentFlow, currentStepIndex - 1);
  }, [currentFlow, currentStepIndex, goToStepIndex]);

  // Non-linear navigation: backward is always free, forward only up to maxReachedIndex -- some
  // steps require action:'navigate' (a real user click on an element), skipping them would
  // break that guarantee. Same order as goToStepIndex (set expectedRouteRef before any possible
  // navigate) so the voluntary jump itself doesn't trigger the manual-exit guard below.
  const jumpToStep = useCallback((index: number) => {
    if (!currentFlow) return;
    if (index < 0 || index > maxReachedIndex) return;
    goToStepIndex(currentFlow, index);
  }, [currentFlow, maxReachedIndex, goToStepIndex]);

  // Triggers the exit confirmation modal -- used both by the pathname navigation guard (effect
  // below) and by TourSpotlight's click-outside (a click on a real link navigates before that
  // effect reacts to the route change).
  const requestExitPrompt = useCallback(() => {
    setExitPromptPending(true);
  }, []);

  // The user chose "Continue the tour" in the exit modal -- return to the route expected
  // by the active step without losing progress. Reuses goToStepIndex (not navigate()
  // directly) because that is the only place that correctly decides needsNav when the
  // active step does not declare `route` (e.g. the welcome step, anchored to page-root
  // on any page) -- in that case there is nowhere to "go back": simply re-resolve the
  // target on the page the user is already on.
  const resumeAfterExitPrompt = useCallback(() => {
    setExitPromptPending(false);
    if (currentFlow) goToStepIndex(currentFlow, currentStepIndex);
  }, [currentFlow, currentStepIndex, goToStepIndex]);

  const confirmPauseFromExitPrompt = useCallback(() => {
    stopTour({ keepProgress: true });
  }, [stopTour]);

  const confirmDismissFromExitPrompt = useCallback(() => {
    stopTour({ keepProgress: false, dismissed: true });
  }, [stopTour]);

  // If the user manually navigates to a route other than what the active step expects,
  // show a confirmation modal instead of silently closing the tour (previously: immediate
  // close saving progress, with no warning -- felt like a bug). Ignores pathname changes
  // caused by the tour itself (selfNavigatingRef, set by goToStepIndex and
  // resumeAfterExitPrompt just before calling navigate()) -- without this flag, every
  // normal step advance with `route` fired the modal on its own navigation.
  useEffect(() => {
    if (selfNavigatingRef.current) {
      selfNavigatingRef.current = false;
      return;
    }
    if (!activeRef.current) return;
    const expected = expectedRouteRef.current?.split('?')[0];
    if (expected && location.pathname !== expected) {
      setExitPromptPending(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => () => clearPoll(), [clearPoll]);

  const steps = currentFlow ? visibleSteps(currentFlow) : [];
  const rawStep = steps[currentStepIndex] || null;
  const currentStep: ResolvedStep | null = rawStep ? { ...rawStep, usingFallback } : null;

  // hasSeenTourServer starts as null (DB has not responded yet) -- in that moment fall
  // back to localStorage so the Home CTA does not flash between "start" and "see again"
  // before the initial sync resolves.
  const hasSeenTour = hasSeenTourServer !== null
    ? hasSeenTourServer
    : (user?.id ? localStorage.getItem(SEEN_KEY_PREFIX + user.id) === '1' : false);

  const value: TourContextType = {
    isActive,
    currentFlow,
    currentStepIndex,
    totalSteps: steps.length,
    currentStep,
    waitingForTarget,
    hasSeenTour,
    savedProgress,
    maxReachedIndex,
    exitPromptPending,
    startTour,
    stopTour,
    nextStep,
    prevStep,
    jumpToStep,
    requestExitPrompt,
    resumeAfterExitPrompt,
    confirmPauseFromExitPrompt,
    confirmDismissFromExitPrompt,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
